import type * as type from "@vTypes";
import { Fragment, useMemo } from "react";
import { cn } from "@/utils/cn";

export default function Comp_manpowerList({
  holidayDates,
  defaultShiftCode,
  activeDate,
  manPower_daily,
  setManpower_daily,
  manPower_monthly,
  filterExceed,
  filterBelow,
  showMaxManpower,
}: {
  holidayDates: string[];
  defaultShiftCode: type.availableShifts[];
  activeDate: { year: number; month: number };
  manPower_daily: type.ManPowerDaily;
  setManpower_daily: React.Dispatch<React.SetStateAction<type.ManPowerDaily>>;
  manPower_monthly: type.ManPowerMonthly;
  filterExceed: boolean;
  filterBelow: boolean;
  showMaxManpower: boolean;
}) {
  const lastDay = new Date(activeDate.year, activeDate.month, 0);
  const daysInMonth = lastDay.getDate();

  // 요일 계산 (월요일 기준 0~6)
  const getDayOfWeek = (day: number) => {
    const date = new Date(activeDate.year, activeDate.month - 1, day);
    return (date.getDay() + 6) % 7; // 0=월, 5=토, 6=일
  };

  const DAY_LABELS = ["월", "화", "수", "목", "금", "토", "일"];

  const getDateStyle = (day: number) => {
    const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dow = getDayOfWeek(day);
    const isHol = holidayDates.includes(dateStr);
    if (dow === 5) return "text-text-sat"; // 토
    if (dow === 6) return "text-text-sun"; // 일
    if (isHol) return "text-text-holiday";
    return "text-text-main2";
  };

  /** 필터 ON일 때 기본(월간 최소) 대비 초과·미만 — 배경 + 테두리 (필터 체크박스·다른 인력 셀과 동일 토큰) */
  const getCellManpowerHighlight = (
    manpower: number,
    defaultManpower: number
  ) => {
    if (filterExceed && manpower > defaultManpower) {
      return "bg-manpower-exceed-bg border-manpower-exceed-border";
    }
    if (filterBelow && manpower < defaultManpower) {
      return "bg-manpower-below-bg border-manpower-below-border";
    }
    return "border-border/50 bg-white";
  };

  const validShifts = useMemo(
    () => defaultShiftCode?.filter((s) => s && s.default_shift !== "O") ?? [],
    [defaultShiftCode]
  );
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth]
  );

  return (
    <div className="w-full overflow-x-auto scrollbar-float">
      <table
        className="border-separate border-spacing-0"
        style={{ minWidth: "max-content" }}
      >
        <thead>
          <tr className="border-b border-border">
            <th className="w-[44px] border-b" />
            <th className="w-[34px] border-b" />
            {days.map((day) => {
              const dow = getDayOfWeek(day);
              return (
                <th
                  key={day}
                  className="w-[32px]  border-b min-w-[32px] px-[2px] pb-[6px] text-center font-pretendard font-medium"
                >
                  <div className="text-[10px] leading-none">
                    <span className={cn(getDateStyle(day), "font-medium")}>
                      {DAY_LABELS[dow]}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "mt-[3px] text-[10px] leading-none",
                      getDateStyle(day)
                    )}
                  >
                    {day}일
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {validShifts?.map((shifts) => {
            const shiftKey = shifts.default_shift || shifts.shift_id;
            const defaultManpower = Number(
              manPower_monthly?.[shiftKey as keyof type.ManPowerMonthly] || 0
            );
            const defaultMaxManpower = Number(
              manPower_monthly?.[
                `${shiftKey}_max` as keyof type.ManPowerMonthly
              ] ?? defaultManpower
            );
            const maxKey = `${shiftKey}_max`;
            const maxValues = manPower_daily?.[maxKey] ?? [];
            const minValues = manPower_daily?.[shiftKey] ?? [];

            return (
              <Fragment key={shifts.shift_id}>
                {showMaxManpower && (
                  <tr key={`${shifts.shift_id}-max`} className="">
                    <td
                      className="align-middle px-[10px] pt-[4px] border-b"
                      rowSpan={showMaxManpower ? 2 : 1}
                    >
                      <div
                        className="flex size-9 items-center justify-center rounded-[5px] text-[15px] font-bold text-white"
                        style={{ backgroundColor: shifts.color || "#999" }}
                      >
                        {shifts.shift_id}
                      </div>
                    </td>
                    <td className="pr-1 text-center text-[11px] pt-2 text-[#666] ">
                      최대
                    </td>
                    {days.map((day, idx) => {
                      const value = maxValues[idx] ?? 0;
                      const minValue = Number(minValues[idx] ?? 0);
                      return (
                        <td
                          key={`${shiftKey}-max-${day}`}
                          className="p-[2px] pt-3"
                        >
                          <input
                            value={Number(value) === 0 ? "-" : value}
                            onChange={(e) => {
                              const parsed = Number(
                                e.target.value.replace(/\D/g, "") || 0
                              );
                              setManpower_daily((prev) => {
                                const current = [
                                  ...(prev[maxKey] ??
                                    Array.from(
                                      { length: daysInMonth },
                                      () => 0
                                    )),
                                ];
                                current[idx] = parsed;
                                return { ...prev, [maxKey]: current };
                              });
                            }}
                            onBlur={() => {
                              const currentMax = Number(value ?? 0);
                              if (currentMax > 0 && currentMax < minValue) {
                                setManpower_daily((prev) => {
                                  const current = [
                                    ...(prev[maxKey] ??
                                      Array.from(
                                        { length: daysInMonth },
                                        () => 0
                                      )),
                                  ];
                                  current[idx] = minValue;
                                  return { ...prev, [maxKey]: current };
                                });
                              }
                            }}
                            className={cn(
                              "size-8 rounded-[5px] border text-sm! font-pretendard text-center leading-none text-[#333] outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                              getCellManpowerHighlight(
                                Number(value),
                                defaultMaxManpower
                              ),
                              Number(value) === 0 && "border-border/50 bg-white"
                            )}
                          />
                        </td>
                      );
                    })}
                  </tr>
                )}
                <tr
                  key={`${shifts.shift_id}-min`}
                  className={cn(!showMaxManpower && "border-t")}
                >
                  {!showMaxManpower && (
                    <td className="align-middle px-[10px] border-b ">
                      <div
                        className="flex h-[22px] w-[22px] items-center justify-center rounded-[5px] text-[15px] font-bold text-white"
                        style={{ backgroundColor: shifts.color || "#999" }}
                      >
                        {shifts.shift_id}
                      </div>
                    </td>
                  )}
                  <td
                    className={cn(
                      "pr-1 text-center text-[11px] text-[#666] border-b"
                      // !showMaxManpower && "pt-3"
                    )}
                  >
                    최소
                  </td>
                  {days.map((day, idx) => {
                    const value = minValues[idx] ?? 0;
                    return (
                      <td
                        key={`${shiftKey}-min-${day}`}
                        className={cn(
                          "p-[2px] border-b",
                          showMaxManpower ? "pb-3" : "pt-3 pb-3"
                        )}
                      >
                        <input
                          value={value}
                          onChange={(e) => {
                            const parsed = Number(
                              e.target.value.replace(/\D/g, "") || 0
                            );
                            setManpower_daily((prev) => {
                              const minValues = (
                                prev[shiftKey] ??
                                Array.from({ length: daysInMonth }, () => 0)
                              ).map((item, itemIdx) =>
                                itemIdx === idx ? parsed : item
                              );
                              const maxKey = `${shiftKey}_max`;
                              const maxValues = [
                                ...(prev[maxKey] ??
                                  Array.from({ length: daysInMonth }, () => 0)),
                              ];
                              const maxValue = Number(maxValues[idx] ?? 0);
                              if (
                                showMaxManpower &&
                                maxValue > 0 &&
                                maxValue < parsed
                              ) {
                                maxValues[idx] = parsed;
                              }
                              return {
                                ...prev,
                                [shiftKey]: minValues,
                                [maxKey]: maxValues,
                              };
                            });
                          }}
                          className={cn(
                            "size-8 rounded-[5px] border text-sm! font-pretendard text-center leading-none text-[#333] outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                            getCellManpowerHighlight(
                              Number(value),
                              defaultManpower
                            )
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
